import type { CountryCode } from 'libphonenumber-js/max/es6';
import type { IntlShape } from 'react-intl';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/max/es6';
import { z } from 'zod';
import { errorCommonI18n } from '@_/i18n-common';
import { isValidPhoneNumber, parsePhoneNumber } from '@_/utils';

const ALL_COUNTRIES = getCountries();

export const phoneNumberSchema = ({
  allowedCountries,
  intl,
  required
}: {
  allowedCountries?: readonly string[];
  intl: IntlShape;
  required?: boolean;
}) => {
  const schema = z
    .object({
      countryCode: z.enum(allowedCountries || ALL_COUNTRIES),
      number: z.string().trim()
    })
    .refine(value => (required ? !!value.number : true), {
      error: intl.formatMessage(errorCommonI18n.required)
    })
    .superRefine((value, ctx) => {
      const { countryCode, number } = value;

      if (!number) {
        return;
      }

      const dialCode = getCountryCallingCode(countryCode as CountryCode);

      if (!isValidPhoneNumber(`+${dialCode}${number}`)) {
        ctx.issues.push({
          code: 'custom',
          input: value,
          params: {
            i18nMessage: errorCommonI18n.invalidPhoneNumber
          }
        });
        return z.NEVER;
      }
    })
    .transform(value => {
      const { countryCode, number } = value;

      if (!number) {
        return null;
      }

      const dialCode = getCountryCallingCode(countryCode as CountryCode);
      const parsedPhoneNumber = parsePhoneNumber(`+${dialCode}${number}`);
      return parsedPhoneNumber.format('E.164');
    });

  return required ? schema : schema.nullish();
};
